import { useEffect, useMemo, useState } from "react";
import type {
  Business,
  BusinessQueryResult,
  BusinessSort,
  Category,
  DirectoryLocation,
} from "@/types/directory";
import {
  getCategories,
  getLocations,
  queryBusinesses,
} from "@/services/directoryService";
import BusinessGrid from "./BusinessGrid";
import SponsoredListings from "./SponsoredListings";
import FeaturedPlacements from "./FeaturedPlacements";
import Pagination from "@/components/base/Pagination";

export interface DirectoryFilterState {
  keyword: string;
  category: string;
  district: string;
  location: string;
  sort: BusinessSort;
}

interface BusinessExplorerProps {
  initialFilters?: Partial<DirectoryFilterState>;
  lockedCategory?: string;
  lockedDistrict?: string;
  lockedLocation?: string;
  pageSize?: number;
  showCategoryFilter?: boolean;
  showDistrictFilter?: boolean;
  /**
   * Show Sponsored rows above the search row and Featured listings just after it
   * (used on category / A–Z category listing pages). When the scope is a
   * category or a keyword, those listings are pulled out of the regular grid so
   * nothing is shown twice.
   */
  showPlacements?: boolean;
}

const selectClass =
  "h-11 w-full cursor-pointer appearance-none rounded-md border border-background-300 bg-background-50 px-4 text-sm text-foreground-800 outline-none transition-colors focus:border-primary-400";

export default function BusinessExplorer({
  initialFilters,
  lockedCategory,
  lockedDistrict,
  lockedLocation,
  pageSize = 9,
  showCategoryFilter = true,
  showDistrictFilter = true,
  showPlacements = false,
}: BusinessExplorerProps) {
  const [categories, setCategories] = useState<Category[]>([]);
  const [locations, setLocations] = useState<DirectoryLocation[]>([]);
  const [filters, setFilters] = useState<DirectoryFilterState>({
    keyword: initialFilters?.keyword ?? "",
    category: lockedCategory ?? initialFilters?.category ?? "",
    district: lockedDistrict ?? initialFilters?.district ?? "",
    location: lockedLocation ?? initialFilters?.location ?? "",
    sort: initialFilters?.sort ?? "relevance",
  });
  const [page, setPage] = useState(1);
  const [result, setResult] = useState<BusinessQueryResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reloadToken, setReloadToken] = useState(0);
  const [sponsored, setSponsored] = useState<Business[]>([]);
  const [featured, setFeatured] = useState<Business[]>([]);
  const [placementsLoading, setPlacementsLoading] = useState(false);

  // Whether the current view is scoped to a category or keyword, which is when
  // the Sponsored / Featured placements make sense to show.
  const hasPlacementScope = Boolean(filters.category || filters.keyword);

  useEffect(() => {
    let active = true;
    Promise.all([getCategories(), getLocations()])
      .then(([categoryList, locationList]) => {
        if (!active) return;
        setCategories(categoryList);
        setLocations(locationList);
      })
      .catch(() => {
        if (!active) return;
        setCategories([]);
        setLocations([]);
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    const excludePlacements = showPlacements && hasPlacementScope;
    queryBusinesses({
      ...filters,
      page,
      pageSize,
      ...(excludePlacements ? { sponsored: false, featured: false } : {}),
    })
      .then((response) => {
        if (active) setResult(response);
      })
      .catch(() => {
        if (active) {
          setError(
            "We could not load businesses right now. Please check your connection and try again.",
          );
          setResult({ items: [], total: 0, page, pageSize });
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [filters, page, pageSize, reloadToken, showPlacements, hasPlacementScope]);

  // Sponsored + Featured placements for the active category / keyword scope.
  useEffect(() => {
    if (!showPlacements || !hasPlacementScope) {
      setSponsored([]);
      setFeatured([]);
      return undefined;
    }
    let active = true;
    setPlacementsLoading(true);
    const scope = {
      keyword: filters.keyword,
      category: filters.category,
      district: filters.district,
      location: filters.location,
    };
    Promise.all([
      queryBusinesses({ ...scope, sponsored: true, page: 1, pageSize: 24 }),
      queryBusinesses({ ...scope, featured: true, sponsored: false, page: 1, pageSize: 24 }),
    ])
      .then(([sponsoredResult, featuredResult]) => {
        if (!active) return;
        setSponsored(sponsoredResult.items);
        setFeatured(featuredResult.items);
      })
      .catch(() => {
        if (!active) return;
        setSponsored([]);
        setFeatured([]);
      })
      .finally(() => {
        if (active) setPlacementsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [
    showPlacements,
    hasPlacementScope,
    filters.keyword,
    filters.category,
    filters.district,
    filters.location,
    reloadToken,
  ]);

  const districts = useMemo(
    () => locations.filter((location) => location.type === "district"),
    [locations],
  );

  const towns = useMemo(
    () =>
      locations.filter(
        (location) =>
          location.type === "town" &&
          (!filters.district || location.districtSlug === filters.district),
      ),
    [locations, filters.district],
  );

  const updateFilter = (patch: Partial<DirectoryFilterState>) => {
    setFilters((previous) => ({ ...previous, ...patch }));
    setPage(1);
  };

  const clearFilters = () => {
    setFilters({
      keyword: "",
      category: lockedCategory ?? "",
      district: lockedDistrict ?? "",
      location: lockedLocation ?? "",
      sort: "relevance",
    });
    setPage(1);
  };

  const total = result?.total ?? 0;
  const rangeStart = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeEnd = Math.min(page * pageSize, total);

  return (
    <div>
      {showPlacements && hasPlacementScope ? (
        <SponsoredListings businesses={sponsored} loading={placementsLoading} />
      ) : null}

      <div className="rounded-lg border border-background-200 bg-background-50 p-4">
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-12">
          <div className="relative lg:col-span-4">
            <span className="pointer-events-none absolute left-4 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-foreground-400">
              <i className="ri-search-line text-base" />
            </span>
            <input
              type="text"
              value={filters.keyword}
              onChange={(event) => updateFilter({ keyword: event.target.value })}
              placeholder="Search by name, service or keyword..."
              className="h-11 w-full rounded-md border border-background-300 bg-background-50 pl-11 pr-4 text-sm text-foreground-800 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
            />
          </div>

          {showCategoryFilter ? (
            <div className="lg:col-span-3">
              <select
                value={filters.category}
                onChange={(event) => updateFilter({ category: event.target.value })}
                className={selectClass}
                aria-label="Filter by category"
              >
                <option value="">All Categories</option>
                {categories.map((category) => (
                  <option key={category.slug} value={category.slug}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>
          ) : null}

          {showDistrictFilter ? (
            <>
              <div className={showCategoryFilter ? "lg:col-span-2" : "lg:col-span-4"}>
                <select
                  value={filters.district}
                  onChange={(event) =>
                    updateFilter({ district: event.target.value, location: "" })
                  }
                  className={selectClass}
                  aria-label="Filter by district"
                >
                  <option value="">All Districts</option>
                  {districts.map((district) => (
                    <option key={district.slug} value={district.slug}>
                      {district.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className={showCategoryFilter ? "lg:col-span-2" : "lg:col-span-3"}>
                <select
                  value={filters.location}
                  onChange={(event) => updateFilter({ location: event.target.value })}
                  className={selectClass}
                  aria-label="Filter by town"
                >
                  <option value="">All Towns</option>
                  {towns.map((town) => (
                    <option key={town.slug} value={town.slug}>
                      {town.name}
                    </option>
                  ))}
                </select>
              </div>
            </>
          ) : null}

          <div className={showDistrictFilter ? "lg:col-span-1" : "lg:col-span-2"}>
            <button
              type="button"
              onClick={clearFilters}
              className="inline-flex h-11 w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-4 text-sm font-semibold text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
            >
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-refresh-line text-base" />
              </span>
              <span className="hidden xl:inline">Reset</span>
            </button>
          </div>
        </div>
      </div>

      {showPlacements && hasPlacementScope ? (
        <FeaturedPlacements businesses={featured} loading={placementsLoading} />
      ) : null}

      <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-foreground-600">
          {loading ? (
            "Searching businesses..."
          ) : error ? (
            "Results unavailable"
          ) : (
            <>
              Showing <span className="font-semibold text-foreground-900">{rangeStart}</span>–
              <span className="font-semibold text-foreground-900">{rangeEnd}</span> of{" "}
              <span className="font-semibold text-foreground-900">{total.toLocaleString("en-IN")}</span>{" "}
              businesses
            </>
          )}
        </p>

        <div className="flex items-center gap-2">
          <span className="whitespace-nowrap text-sm text-foreground-600">Sort by</span>
          <select
            value={filters.sort}
            onChange={(event) => updateFilter({ sort: event.target.value as BusinessSort })}
            className="h-10 cursor-pointer appearance-none rounded-md border border-background-300 bg-background-50 px-4 pr-8 text-sm text-foreground-800 outline-none transition-colors focus:border-primary-400"
            aria-label="Sort results"
          >
            <option value="relevance">Relevance</option>
            <option value="rating">Highest rated</option>
            <option value="newest">Newest first</option>
          </select>
        </div>
      </div>

      {error ? (
        <div className="mt-6 flex items-start justify-between gap-3 rounded-lg bg-rose-50 px-4 py-3 text-sm text-rose-700">
          <span className="flex items-start gap-2.5">
            <i className="ri-error-warning-line mt-0.5 shrink-0 text-base" />
            {error}
          </span>
          <button
            type="button"
            onClick={() => setReloadToken((token) => token + 1)}
            className="shrink-0 cursor-pointer whitespace-nowrap rounded-md border border-rose-300 px-3 py-1 text-xs font-semibold text-rose-700 transition-colors hover:bg-rose-100"
          >
            Retry
          </button>
        </div>
      ) : (
        <div className="mt-6">
          <BusinessGrid businesses={result?.items ?? []} loading={loading} />
        </div>
      )}

      <Pagination page={page} pageSize={pageSize} total={total} onPageChange={setPage} />
    </div>
  );
}